import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Lock, LogOut, Menu, Moon, ShieldCheck, Sun } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { LogoWordmark, LogoMark } from './Logo';
import { VoiceCommandButton } from './VoiceCommand';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Tour } from './Tour';
import AccountNotices from './AccountNotices';
import { gainInk, SandboxBadge } from './ui';
import { useAuth } from '../state/AuthContext';
import { useTheme } from '../state/ThemeContext';
import { useT } from '../i18n';
import { useEntitlements, TIER_ORDER, LEGACY_TIER_ALIAS, type FeatureKey } from '../lib/useEntitlements';
import { tierById, withGst } from '../lib/plans';
import { formatPaiseInr } from '../lib/format';
import { tierLabel, isTopTier } from '../lib/labels';
import { streamUrl } from '../lib/api';
import type { Tick } from '../lib/types';
import { formatDate, formatNumber, formatPct } from '../lib/format';
import { NAV_BAR, navGroupsFor, type NavKey } from '../lib/nav';
import { WEB_NAV } from './navTargets';

// A nav destination. `gate` names the LOWEST-tier feature on the page: the tier
// badge shows (with the server's own tier name) ONLY when the session can't reach
// that entry. Set it ONLY on pages that are fully walled — a page with ANY free
// content (Explore's nominees/glossary, Robo's risk profile, Trackers' learn&earn)
// must stay ungated here or the badge scares users off content they can use, the
// exact "walls say random tiers" complaint this is meant to fix.
type NavItem = { key: NavKey; to: string; label: string; icon: LucideIcon; end?: boolean; gate?: FeatureKey };

const navItem = (key: NavKey, label: string): NavItem => ({ key, label, ...WEB_NAV[key], end: key === 'home' });

// Conv-29: a per-nav tier signal so a user sees which surfaces hit a wall before
// clicking. The tier name comes ONLY from the server's featureTiers (tierFor) —
// never a hand-typed literal — so it self-heals when a tier moves. Founders/paid
// users resolve can()===true and the badge auto-hides.
function TierBadge({ feature }: { feature: FeatureKey }) {
  const t = useT();
  const { can, tierFor, access } = useEntitlements();
  if (can(feature)) return null;
  // A LAPSE IS NOT A TIER WALL — the Conv-37 rule, which LockedFeature and
  // LockedPage both branch on and this badge did not.
  //
  // `can()` folds in `access.active`, so a lapsed account fails can() on EVERY
  // feature and this badge lit up on every gated rail row at once, each naming a
  // tier. For someone who owes one renewal that is wrong twice: it says "upgrade"
  // when the answer is "pay what you already owe", and on rows whose floor sits
  // BELOW the tier they actually hold it names a tier they are already past. The
  // reason for a lock has to match the lock. The subscription bar directly above
  // the rail already states the lapse, so the honest badge here is none.
  if (!access.active) return null;
  const tier = tierFor(feature);
  // A lock icon (no micro-text — stays above the 12px legibility floor). The tier
  // name rides the tooltip + aria-label, and comes ONLY from the server's tierFor
  // (never a hand-typed literal), so it self-heals when a tier moves.
  // Conv-40: no "or higher" on the top tier — there is no plan above Platinum.
  const TOP_TIER = TIER_ORDER[TIER_ORDER.length - 1];
  const tierText = tier ? t(tierLabel(tier)) : '';
  const label = tier
    ? isTopTier(tier, TOP_TIER) || isTopTier(tierText, TOP_TIER)
      ? tierText
      : t('{tier} or higher', { tier: tierText })
    : t('Requires a higher tier');
  return (
    <span className="ml-auto shrink-0" title={label} aria-label={label}>
      <Lock size={13} className="text-ink-faint dark:text-slate-400" aria-hidden="true" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// ONE MAP, BOTH CLIENTS (founder order, 2026-10-20).
// ---------------------------------------------------------------------------
// The bar, the "More" groups, their rows, labels and order all come from the
// shared model in src/lib/nav.ts, which the phone vendors byte-identically — so a
// customer moving between the website and the app meets the same five tabs and
// the same list, in the same words. Only the route and the icon are web facts
// (components/navTargets.ts). Before this the web bar held six (Home, Portfolio,
// Progress, Connect, Markets, Learn) and the phone five, in a different order.
//
// Bottom bar (<1024px) = the model's five, Settings last; the narrow header's
// gear went with it, because Settings is now a tab. The desktop rail shows the
// first four, then the More groups, with Settings under Account.
//
// 🔴 EVERY ROW IS UNGATED, deliberately (Conv-49): every destination here holds
// free content (the mixed-page trap), and the licence-dark pages behind Roadmap
// open on NO plan, so a tier badge there would name a tier for a surface no tier
// opens. Badge a row ONLY after verifying the page renders nothing outside a
// LockedFeature/can().
//
// Data & Privacy stays on the rail (Account) AND in the narrow header shield: it
// is a DPDP data-rights surface and must be one click from anywhere.
// The bar's last tab is labelled "More" and draws a menu icon; the rail's
// Settings row (under Account) keeps the gear.
const TABS: NavItem[] = NAV_BAR.map((b) => (b.key === 'settings' ? { ...navItem(b.key, b.label), icon: Menu } : navItem(b.key, b.label)));
const RAIL_PRIMARY: NavItem[] = TABS.filter((i) => i.key !== 'settings');

type NavGroup = { heading: string; items: NavItem[] };
const railGroups = (founder: boolean, staffOnly = false, minor = false): NavGroup[] =>
  navGroupsFor({ web: true, founder, staffOnly, minor }).map((g) => ({
    heading: g.heading,
    items: g.items.map((i) => navItem(i.key, i.label)),
  }));

// ---------------------------------------------------------------------------
// Conv-37 — the trial / renewal / lapse bar.
// ---------------------------------------------------------------------------
// ONE compact bar, and it never appears for a FOUNDER or a plain ACTIVE
// subscription — a permanent banner on a healthy account is noise that trains
// people to skip the one day it matters.
//
// Each state says what is TRUE and what the next step is, and nothing else:
//   TRIALING  — days left, and what happens when they run out.
//   GRACE     — the period ended, access continues until a stated date.
//   HELD_OPEN — THE PLAN LAPSED, on a stated date, and paid plans are not open
//               yet so access continues. It must never read as "all is well":
//               the whole point of held-open is that something DID happen.
//   EXPIRED   — the plan ended; the only action is to choose a plan.
//   cancelled — active until a stated date, and it will not renew. This is
//               checked FIRST on the still-active states, because "ends in 12
//               days and renews monthly" is false once the user has cancelled.
//
// Copy rule: state the fact and the operational next step. No urgency framing,
// no "don't lose your data", nothing shaped like advice.
function SubscriptionBar() {
  const t = useT();
  const { access, isFounder } = useEntitlements();
  const { session } = useAuth();
  const days = access.daysRemaining;
  const endsOn = access.endsAt ? formatDate(access.endsAt) : null;

  if (isFounder || access.state === 'FOUNDER') return null;
  // An under-18 account is on the free plan and can never buy one (minor-accounts.mjs).
  if (session?.minor) return null;
  if (access.state === 'ACTIVE' && !access.cancelled) return null;

  // The trial-end sentence, with no interpolated key: "in {n} days" reads wrong
  // at 1 and at 0, and a pluralised English string built by concatenation would
  // have no key any catalog could carry.
  //
  // Conv-56 fixes 5+15 — the rewrite the compare-sim filed as the most-repeated
  // major across cohorts. The old line said "your plan renews monthly unless you
  // cancel" with no plan name, no price and no "no card on file", above the net
  // worth, on every page — it read as an active debit threat to exactly the
  // anxious users the trial is meant to keep. And Fix 15's "banner on a Platinum
  // account" is this bar being TRUE while UNSPECIFIC: a sandbox plan-pick keeps
  // the trial state deliberately (restart = unlimited free trial, cancel =
  // punishing a look), so the honest repair is specificity — the bar now names
  // the tier, the rupee price (from the plans twin, never hand-typed), and the
  // fact that nothing can be charged while no card is stored.
  const trialLine = () => {
    const plan = tierById(LEGACY_TIER_ALIAS[session?.entitlements?.tier ?? 'FREE'] ?? session?.entitlements?.tier ?? 'FREE');
    const planName = plan?.name ?? 'Sansara';
    // Paise → ₹ via the never-converting formatter — a statutory-shape figure,
    // not the user's own money, so display currency must not touch it.
    const price = plan ? withGst(formatPaiseInr(plan.pricing.monthly)) : '';
    if (days === null) return t('Your free month of {plan} is running. After it ends, {plan} costs {price} a month. No card is on file — nothing is charged automatically.', { plan: planName, price });
    if (days <= 0) return t('Your free month of {plan} ends today. After that, {plan} costs {price} a month. No card is on file — nothing is charged automatically.', { plan: planName, price });
    if (days === 1) return t('Your free month of {plan} ends tomorrow. After that, {plan} costs {price} a month. No card is on file — nothing is charged automatically.', { plan: planName, price });
    return t('Your free month of {plan} ends in {days} days. After that, {plan} costs {price} a month. No card is on file — nothing is charged automatically.', { plan: planName, days: formatNumber(days), price });
  };

  let tone = 'info';
  let message: string;
  let cta = t('See plans');

  if (access.state === 'EXPIRED') {
    tone = 'warn';
    message = access.expiredFrom === 'TRIAL'
      ? t('Your free month has ended. Choose a plan to carry on.')
      : t('Your plan has ended. Choose a plan to carry on.');
    cta = t('Choose a plan');
  } else if (access.heldOpen) {
    tone = 'warn';
    message = endsOn
      ? t('Your plan lapsed on {date}. Paid plans open at launch, so your access continues until then.', { date: endsOn })
      : t('Your plan has lapsed. Paid plans open at launch, so your access continues until then.');
  } else if (access.cancelled) {
    message = endsOn
      ? t('You have cancelled. Your plan stays active until {date} and will not renew.', { date: endsOn })
      : t('You have cancelled. Your plan stays active until the end of the paid period and will not renew.');
    cta = t('Manage plan');
  } else if (access.state === 'TRIALING') {
    message = trialLine();
    cta = t('Manage plan');
  } else if (access.state === 'GRACE') {
    tone = 'warn';
    const graceOn = access.graceEndsAt ? formatDate(access.graceEndsAt) : null;
    message = graceOn
      ? t('Your last payment has not gone through. Your access continues until {date}.', { date: graceOn })
      : t('Your last payment has not gone through. Your access continues for now.');
    cta = t('Manage plan');
  } else {
    return null;
  }

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b px-4 py-2 text-xs font-semibold sm:px-6 ${
        // Same token pairs the Callout component uses, so the bar is audited by
        // the same contrast pass and can never drift from the app's tones.
        tone === 'warn'
          ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'
          : 'border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-200'
      }`}
    >
      <span className="min-w-0 flex-1">{message}</span>
      {/* `data-tour="plans"` — the Conv-48 onboarding card about what a padlock
          means anchors HERE rather than on the Plans nav row, because the rail is
          `hidden … lg:flex` and no `nav:/app/*` target outside TABS exists on a
          phone. This bar renders at every width for every state that is not a
          plain healthy ACTIVE subscription, which is every first-run account. */}
      <NavLink to="/app/plans" data-tour="plans" className="shrink-0 underline underline-offset-2">
        {cta}
      </NavLink>
    </div>
  );
}

function TickerStrip() {
  const [ticks, setTicks] = useState<Tick[]>([]);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    const source = new EventSource(streamUrl(['NIFTY50', 'SENSEX', 'BANKNIFTY', 'NIFTYIT', 'RELIANCE', 'TCS', 'HDFCBANK', 'INFY']), { withCredentials: true });
    source.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data) as { ticks: Tick[] };
        setTicks(payload.ticks);
      } catch {
        /* ignore malformed tick */
      }
    };
    sourceRef.current = source;
    return () => source.close();
  }, []);

  if (!ticks.length) return null;
  // Sim night 23 (30 Oct 2026): the ticker sat on every page showing simulated
  // index levels with no mark while the page body below carried the chip — 142
  // expert critics read it as a live feed. The mark sits OUTSIDE the scrolling
  // track so it never scrolls away, and comes from the ticks' own flag.
  const simulated = ticks.some((tk) => tk.sandbox);
  // Two identical groups. Each carries its OWN trailing gap (pr-*) equal to the
  // internal gap, so the track is exactly two group-widths wide and the
  // translateX(-50%) loop lands precisely on the seam — no jump. (The old flat
  // duplicate with px padding + variable item widths made -50% miss the seam,
  // which read as a stutter once per loop.)
  //
  // THE TRACK'S WIDTH MUST NOT DEPEND ON THE TICK VALUES. The loop translates by
  // -50% *of this element's own width*, so if a live tick makes any label wider
  // or narrower, the animation's end-point moves and the marquee visibly jumps
  // mid-flight. Measured on the live SSE feed before this fix: 7 width changes
  // in 12s, a 31.68px spread and a 15.84px worst-case jump — every ~1.7s. That
  // is the "jerking / stopping then continuing" report, and it is a DIFFERENT
  // bug from the seam one above (which fires once per 45s loop) — which is why
  // fixing the seam did not make it go away.
  //
  // `.num` (tabular-nums) equalises digit WIDTH but not digit COUNT, and it is
  // the count that moves here: formatPct emits "0.00%" (5 chars, no sign),
  // "+0.08%" (6) and "+10.08%" (7) — the deltas measured were exactly 1-2
  // characters x 2 copies. So each live field gets a fixed-width slot sized to
  // its realistic maximum; with tabular-nums, `ch` is the digit width, so the
  // reservation is exact. The symbol needs no slot: symbols never change.
  return (
    <div
      className="flex items-center border-b border-slate-200 bg-surface dark:border-slate-800 dark:bg-surface-dark"
      aria-hidden="true"
    >
      {simulated ? <span className="shrink-0 pl-2 sm:pl-3"><SandboxBadge label="Simulated" /></span> : null}
      <div className="ticker-mask min-w-0 flex-1 overflow-hidden">
        <div className="ticker-track flex w-max py-1.5">
          {[0, 1].map((copy) => (
            <div key={copy} className="flex shrink-0 gap-6 pr-6 sm:gap-8 sm:pr-8">
              {ticks.map((t, i) => (
                <span key={`${copy}-${t.symbol}-${i}`} className="num flex items-center gap-2 whitespace-nowrap text-xs">
                  <span className="font-bold text-ink-soft dark:text-slate-300">{t.symbol}</span>
                  {/* 10ch: widest realistic index print is "81,710.32" (9ch), +1 headroom. */}
                  <span className="min-w-[10ch] text-right font-semibold">{formatNumber(t.last)}</span>
                  {/* 8ch: worst case "-100.00%" (8ch); covers the sign appearing/vanishing at 0. */}
                  <span
                    className={`min-w-[8ch] text-right ${gainInk(t.changePct)}`}
                  >
                    {formatPct(t.changePct)}
                  </span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Comprehensive SEBI-style risk warning shown persistently in the footer.
// Conv-26: rendered in FULL, always — no line-clamp. Mandatory disclosure copy
// must never truncate (the sim's six-night #1 finding caught it clamped
// mid-sentence on iPhone, "physically unfinishable").
const RISK_WARNING =
  'Sansara shares factual data only — not SEBI-registered investment advice. Investments carry market risks; read all documents carefully. Past performance does not guarantee future returns.';

export function Avatar({ session, size = 36 }: { session: { avatarUrl?: string | null; initials?: string } | null; size?: number }) {
  const dimension = { width: size, height: size };
  if (session?.avatarUrl) {
    return (
      <img
        src={session.avatarUrl}
        alt=""
        style={dimension}
        className="shrink-0 rounded-full object-cover"
      />
    );
  }
  return (
    <span
      style={dimension}
      className="flex shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-white"
    >
      {session?.initials ?? '··'}
    </span>
  );
}

// Conv-26 REVIEW RULING: the header language toggle built earlier this batch
// was REMOVED before merge. The web app has no Hindi strings yet, so a
// prominent "हिं" button would change nothing visible — the exact
// pref-that-claims-to-work class as the mobile currency-picker bug (Conv-22).
// The sim's real ask ("surface the language switch") is honoured only when
// switching does something: a Hindi/Bengali translation pass is its own
// batch, queued for the founder. The Settings picker stays (mobile reads it).

// Conv-26: client-side idle watcher. The SERVER idle timeout covers closed
// tabs, but an OPEN tab's background polls (Markets refetches alerts every
// 60s) reset the server clock forever — the review caught that a tab left
// open on a desk never times out. This watcher tracks real USER input; after
// the same 15 minutes without any, it signs out locally (the login page then
// explains why). Polls may keep the server session warm, but the screen locks.
// Keep this in step with the server default (SANSARA_IDLE_TIMEOUT_MINUTES).
const CLIENT_IDLE_MS = 15 * 60 * 1000;
const ACTIVITY_KEY = 'sansara.lastActivity';
function useIdleSignout(logout: () => Promise<void>) {
  const navigate = useNavigate();
  useEffect(() => {
    // Activity is tracked ACROSS TABS via localStorage: input events only fire
    // in the focused document, but logout() revokes the ONE shared cookie
    // session — per-tab tracking let a background tab sign out a user who was
    // typing in another tab (diff-verify HIGH). The shared stamp means "no
    // Sansara tab saw input for 15 minutes", which is what idle actually means.
    let last = Date.now();
    const readShared = () => {
      try { const v = Number(localStorage.getItem(ACTIVITY_KEY)); return Number.isFinite(v) ? v : 0; } catch { return 0; }
    };
    const touch = () => {
      last = Date.now();
      try { localStorage.setItem(ACTIVITY_KEY, String(last)); } catch { /* storage unavailable — per-tab fallback */ }
    };
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    for (const e of events) window.addEventListener(e, touch, { passive: true });
    const timer = window.setInterval(() => {
      const lastAnywhere = Math.max(last, readShared());
      if (Date.now() - lastAnywhere >= CLIENT_IDLE_MS) {
        window.clearInterval(timer);
        try { sessionStorage.setItem('sansara.sessionLost', '1'); } catch { /* storage unavailable */ }
        void logout().finally(() => navigate('/auth'));
      }
    }, 30_000);
    return () => {
      window.clearInterval(timer);
      for (const e of events) window.removeEventListener(e, touch);
    };
  }, [logout, navigate]);
}

export default function AppShell() {
  const { session, logout, refreshSession } = useAuth();
  const { theme, toggle } = useTheme();
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useIdleSignout(logout);

  // Conv-37: a 402 SUBSCRIPTION_EXPIRED means the session is fine but the plan
  // ended mid-visit. Re-read the session so `entitlements.access` — and with it
  // the bar above and every can() in the app — reflects the state the SERVER is
  // enforcing, instead of a page full of failed requests that still looks paid.
  useEffect(() => {
    const onExpired = () => { void refreshSession(); };
    window.addEventListener('sansara:subscription-expired', onExpired);
    return () => window.removeEventListener('sansara:subscription-expired', onExpired);
  }, [refreshSession]);

  const groups = railGroups(Boolean(session?.isFounder || session?.staffPermissions?.length), !session?.isFounder && Boolean(session?.staffPermissions?.length), Boolean(session?.minor));

  // 🔴 SIXTEEN ROWS + FOUR HEADINGS MUST FIT AT 1280×900 (2026-10-20). The
  // shared nav model put Connect, Learn, Robo, Sansara AI and Calculators on
  // the rail, and at the old spacing (primary py-2.5, secondary py-1.5, gap-1,
  // headings pt-2) the rail overflowed by 77px MEASURED — Settings, the last
  // row, sat below the fold, the exact Conv-49 defect. Rows are now 32px
  // (secondary) / 36px (primary) with tighter gaps; still a comfortable
  // pointer target on a desktop, which is the only place the rail renders (the
  // bottom bar has its own, taller classes). Re-measure if you add a row.
  // The founder account carries one more row (Founder console): measured at
  // 1280×900 it fitted with 7px to spare (scrollHeight == clientHeight), so
  // the aside's py-6 → py-5 and the account block's mt-4/pt-4 → mt-3/pt-3
  // (2026-10-20) buy ~16px more; past that the nav scrolls, never clips.
  const navGroupLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-xl px-3.5 py-1 text-sm font-semibold transition-colors ${
      isActive
        ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
        : 'text-ink-soft hover:bg-slate-100 hover:text-ink dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
    }`;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-xl px-3.5 py-1.5 text-sm font-semibold transition-colors ${
      isActive
        ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
        : 'text-ink-soft hover:bg-slate-100 hover:text-ink dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
    }`;

  return (
    <div className="flex h-dvh overflow-hidden">
      {/* Desktop sidebar — always visible, like a fixed left panel */}
      <aside className="hidden h-full w-64 shrink-0 flex-col border-r border-slate-200 bg-surface px-4 py-5 dark:border-slate-800 dark:bg-surface-dark lg:flex">
        <NavLink to="/app" className="px-2">
          <LogoWordmark />
        </NavLink>
        {/* min-h-0 + overflow-y-auto keeps the logo (above) and account block
            (below) pinned; the nav only scrolls as a last resort on very short
            screens so items are never cut off the screen. */}
        {/* 🔴 mt-8 → mt-4, AND THE HEADINGS BELOW pt-4 → pt-2, BECAUSE A SCREENSHOT
            SHOWED `Settings` — the fourteenth and LAST row — SCROLLED OUT OF SIGHT
            at 1280×900. Conv-49 took the rail from 23 rows to 14 and the comment
            below (written when it held 23) had stopped being true: the rail no
            longer "scrolls whatever we do", it now misses fitting by ONE ROW, so
            the last destination simply looked absent. Measured off the capture:
            the account block's divider sits at y≈729 and Settings wanted y≈736.
            The 40px recovered here clears it with room to spare.
            Nothing automated could see this — tsc, eslint, both i18n gates, 704
            server tests and audit-contrast are all blind to a nav row that renders
            correctly just below the fold. Re-check it if you add a row back. */}
        <nav className="mt-3 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pr-1" aria-label={t('Primary')}>
          {RAIL_PRIMARY.map(({ to, label, icon: Icon, end, gate }) => (
            <NavLink key={to} to={to} end={end} data-tour={`nav:${to}`} className={navLinkClass}>
              <Icon size={18} strokeWidth={2.2} />
              {t(label)}
              {gate ? <TierBadge feature={gate} /> : null}
            </NavLink>
          ))}
          {/* Named groups, no disclosure. The heading is a real <h2> so a screen
              reader announces the grouping the sighted reader gets for free;
              text-xs is the 12px legibility floor the contrast audit enforces,
              not a size chosen for looks. */}
          {/* One heading per group and NO rule between them: a heading already
              reads as a break, and stacking both cost ~100px. The account block
              below (avatar → Settings, sign out) is pinned OUTSIDE this scroller,
              so the two controls a stuck user reaches for are never the ones
              off-screen — but that pin is exactly why the SCROLLER's own last row
              can hide, which is what happened to Settings. See the note on <nav>. */}
          {groups.map(({ heading, items }) => (
            <div key={heading}>
              <h2 className="px-3.5 pb-0.5 pt-1.5 text-xs font-bold uppercase tracking-wide text-ink-faint dark:text-slate-400">
                {t(heading)}
              </h2>
              <div className="flex flex-col gap-0.5">
                {items.map(({ to, label, icon: Icon, gate }) => (
                  <NavLink key={to} to={to} data-tour={`nav:${to}`} className={navGroupLinkClass}>
                    <Icon size={18} strokeWidth={2.2} />
                    {t(label)}
                    {gate ? <TierBadge feature={gate} /> : null}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="mt-3 space-y-3 border-t border-slate-200 pt-3 dark:border-slate-800">
          <NavLink
            to="/app/settings"
            className="flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label={t('Account settings')}
          >
            <Avatar session={session} size={36} />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{session?.displayName}</p>
              {/* The server sends the DISPLAY name ("Gold Elite") — the English name IS
                  the i18n key, so it must go through t() or the tier line stays English
                  while every other string on the shell has switched. */}
              {/* Conv-37: no 'Free' fallback — there is no free tier to fall back
                  to, and printing one would name a plan that does not exist. An
                  absent name renders nothing. */}
              <p className="truncate text-xs text-ink-faint dark:text-slate-400">{session?.subscription.name ? t(tierLabel(session.subscription.name)) : null}</p>
            </div>
          </NavLink>
          {/* TWO ROWS, not four items in one (Conv-40, founder: "bottom left buttons
              alignment / appearance is not appealing, incorrect").

              Four controls never fit the w-64 rail: two icon buttons plus two
              labelled ones left the labels ~55px each, so "Sign out" broke across
              two lines and dragged its own row taller than the icons beside it —
              the icons then read as vertically mis-set against a two-line
              neighbour. Making the labels smaller would have pushed them under the
              12px legibility floor the contrast audit enforces, so the fix is
              layout, not type size.

              Row 1 keeps the three quick toggles (voice, language, theme); Sign out
              gets its own full-width row, which is also the right emphasis for the
              only destructive control in the group. `whitespace-nowrap` is the
              guarantee: a longer translation (German-length, or Tamil) now widens
              or truncates rather than silently re-wrapping the row. */}
          <div className="space-y-1 px-1">
            <div className="flex items-center gap-1">
              <VoiceCommandButton compact />
              <LanguageSwitcher compact />
              <button className="btn-ghost !py-2 flex-1 justify-center whitespace-nowrap text-xs" onClick={toggle}>
                {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
                {t(theme === 'light' ? 'Dark' : 'Light')}
              </button>
            </div>
            <button
              className="btn-ghost !py-2 w-full justify-center whitespace-nowrap text-xs"
              onClick={async () => {
                await logout();
                navigate('/');
              }}
            >
              <LogOut size={15} />
              {t('Sign out')}
            </button>
          </div>
        </div>
      </aside>

      {/* Main column — persistent header + footer, main scrolls between them */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Persistent header: mobile brand/controls (lg:hidden) + market ticker (all breakpoints) */}
        <header className="pt-safe z-40 shrink-0 border-b border-slate-200 bg-surface dark:border-slate-800 dark:bg-surface-dark">
          <div className="flex items-center justify-between px-4 py-3 lg:hidden">
            <NavLink to="/app">
              <span className="inline-flex items-center gap-2">
                <LogoMark size={24} />
                <span className="font-display text-lg font-bold">Sansara</span>
              </span>
            </NavLink>
            <div className="flex items-center gap-1">
              <VoiceCommandButton compact />
              <LanguageSwitcher compact />
              <button className="btn-ghost rounded-lg p-2" onClick={toggle} aria-label={t('Toggle theme')}>
                {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
              </button>
              {/* 🔴 CONV-48 — DATA & PRIVACY, REACHABLE ON A PHONE AT LAST.
                  The rail is `hidden … lg:flex` and the bottom bar renders TABS
                  only, so below 1024px `/app/privacy` had exactly ONE way in:
                  Settings → More, two taps deep and undiscoverable. On the app
                  whose central claim is that you can see who holds your data.

                  It also silently broke the onboarding tour. `Tour.tsx` spotlights
                  `[data-tour="nav:/app/privacy"]`, that node did not exist on a
                  phone, and the tour's "skip a step whose target is missing" rule
                  then dropped it — measured, 9 cards on desktop against 7 on a
                  phone, with the TRUST card among the two missing. It had been
                  invisible to every phone user since the tour shipped.

                  Carrying the same `data-tour` attribute is all the fix the tour
                  needs: `findTarget` filters to nodes with real SIZE, so the
                  `lg:hidden` header node is skipped on desktop (the rail wins) and
                  the rail is skipped here. One anchor per width, no Tour change.

                  This is a DPDP data-rights surface, not a preference, and burying
                  it inside the settings screen is what made it unreachable in the
                  first place. (2026-10-20: the gear that sat beside it went —
                  Settings is a bottom-bar tab now, on both clients.) */}
              <NavLink
                to="/app/privacy"
                data-tour="nav:/app/privacy"
                className="btn-ghost rounded-lg p-2"
                aria-label={t('Data & Privacy')}
              >
                <ShieldCheck size={18} />
              </NavLink>
            </div>
          </div>
          <TickerStrip />
          <SubscriptionBar />
        </header>

        {/* Only the main content scrolls, between the fixed header and footer */}
        <main className="app-scroll min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-shell px-4 sm:px-6">
            <Outlet />
          </div>
          {/* PHONE: the same warning, in FULL, at the end of every page's content
              (founder 2026-10-31, after sim night 24: pinned, it held ~85px of every
              390px screen — 104 critics). Still on every view and never clamped
              (the Conv-26 rule); it now scrolls with the page instead of sitting
              above the tab bar. Desktop keeps the pinned footer below. */}
          <p className="mx-auto mt-6 flex w-full max-w-shell items-start gap-2 border-t border-slate-200 px-4 py-3 text-xs leading-snug text-ink-soft dark:border-slate-800 dark:text-slate-300 sm:px-6 lg:hidden">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-700 dark:text-brand-400" />
            <span className="min-w-0 flex-1">{t(RISK_WARNING)}</span>
          </p>
        </main>

        {/* Persistent footer: comprehensive market-risk warning, on every view.
            Conv-26 (sim nights 1-6, the settled #1 finding): this strip was
            11px ink-faint with a line-clamp that cut the disclaimer
            MID-SENTENCE on phones — a regulatory-surface defect for a
            disclosure-based pre-licence app. The warning now WRAPS in full
            (never clamp mandatory disclosure copy) at a readable size and
            AA-safe ink. */}
        <footer className="hidden shrink-0 border-t border-slate-200 bg-surface px-4 py-2.5 dark:border-slate-800 dark:bg-surface-dark sm:px-6 lg:block">
          <p className="flex items-start gap-2 text-xs leading-snug text-ink-soft dark:text-slate-300">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-700 dark:text-brand-400" />
            <span className="min-w-0 flex-1">{t(RISK_WARNING)}</span>
          </p>
        </footer>

        {/* Mobile tab bar — in-flow below the footer so it never overlaps content */}
        <nav
          className="pb-safe z-40 shrink-0 border-t border-slate-200 bg-surface dark:border-slate-800 dark:bg-surface-dark lg:hidden"
          aria-label={t('Primary')}
        >
          {/* C27 (2026-10-20): equal columns broke Kannada "Markets" and the
              Portfolio label in ta/te/kn/ml mid-word — 65px each at 390px.
              Each column now starts at its label's one-line width and the rest
              of the bar is shared equally (flex: 1 1 auto). The six labels
              then needed ~334px at 390 in the longest of the ten languages
              (measured), so nothing wraps; the bar is five since 2026-10-20. It can still SHRINK: under a large
              browser font, or below ~360px, a column narrows and its label
              wraps inside it — never a bar wider than the screen, which the
              root's overflow-hidden would clip (review of the first, grid
              version: max-content tracks cannot shrink, so Learn was cut off). */}
          <div className="flex">
            {TABS.map(({ to, label, icon: Icon, end, gate }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-tour={`nav:${to}`}
                className={({ isActive }) =>
                  // min-w-0 + a wrapping label (C18, 2026-10-19): a Tamil or
                  // Malayalam label wider than its column used to spill into its
                  // neighbours ("போர்ட்ஃபோலியோமுன்னேற்றம்"). The column fits its
                  // label when the bar has room (C27); when it does not, the label
                  // wraps INSIDE its own column. 12px stays the floor.
                  `flex min-w-0 flex-auto flex-col items-center gap-1 px-px py-2 text-xs min-[390px]:px-1 font-semibold tracking-tight transition-colors ${
                    isActive ? 'text-brand-700 dark:text-brand-400' : 'text-ink-faint dark:text-slate-400'
                  }`
                }
              >
                <Icon size={20} strokeWidth={2.2} className="shrink-0" />
                <span className="flex max-w-full flex-wrap items-center justify-center gap-1 text-center leading-tight [overflow-wrap:anywhere]">
                  {t(label)}
                  {gate ? <TierBadge feature={gate} /> : null}
                </span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
      {/* DPDP 2026-10-29: the one-time age question and unread breach/privacy
          notices (server/routes-dpdp.mjs). Shown over whichever screen the user
          lands on after sign-in. */}
      <AccountNotices />
      {/* Mounted last so the spotlight overlays the sidebar, main and tab bar alike. */}
      <Tour />
    </div>
  );
}
